<template>
  <el-dialog
    title="选择培训资料"
    :visible.sync="dialogVisible"
    width="800px"
    @close="handleClose"
    append-to-body
  >
    <div class="materials-selector">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-input
          v-model="searchForm.title"
          size="small"
          placeholder="搜索资料标题"
          style="width: 300px"
          @keyup.enter="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>

        <el-cascader
          v-model="searchForm.category"
          :options="trainingCategoryTree"
          :props="categoryFilterProps"
          size="small"
          placeholder="选择分类"
          clearable
          filterable
          style="width: 220px; margin-left: 10px"
          @change="handleSearch"
        />
      </div>

      <!-- 资料列表 -->
      <el-table
        :data="materialsList"
        size="small"
        @selection-change="handleSelectionChange"
        style="width: 100%; margin-top: 16px"
      >
        <el-table-column type="selection" width="55" />

        <el-table-column prop="title" label="资料标题" min-width="200">
          <template slot-scope="scope">
            <div class="title-cell">
              <span class="title-text">{{ scope.row.title }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="category" label="分类" min-width="160">
          <template slot-scope="scope">
            <el-tag size="small">{{ trainingCategoryText(scope.row.category) || "未分类" }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="estimatedDuration" label="预计时长" width="100">
          <template slot-scope="scope">
            <span v-if="scope.row.estimatedDuration"> {{ scope.row.estimatedDuration }}分钟 </span>
            <span v-else class="text-muted">未设置</span>
          </template>
        </el-table-column>

        <el-table-column prop="enabled" label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.enabled ? 'success' : 'danger'" size="mini">
              {{ scope.row.enabled ? "启用" : "禁用" }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          small
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
        />
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">取消</el-button>
      <el-button size="small" type="primary" @click="handleConfirm" :disabled="selectedMaterials.length === 0">
        确定
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getTrainingMaterialsList } from "@/api/new_training";
import { ParamUtil } from "@/util";
import trainingCategoryMixin from "../utils/trainingCategory";
export default {
  name: "MaterialsSelectorDialog",
  mixins: [trainingCategoryMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    category: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      dialogVisible: false,
      materialsList: [],
      selectedMaterials: [],

      // 搜索表单
      searchForm: {
        title: "",
        category: ""
      },

      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      }
    };
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.searchForm = {
            title: "",
            category: this.category || ""
          };
          this.pagination.currentPage = 1;
          this.selectedMaterials = [];
          this.loadMaterialsList();
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载资料列表
    async loadMaterialsList() {
      try {
        const params = {
          page: {
            current: this.pagination.currentPage,
            size: this.pagination.pageSize
          },
          param: ParamUtil.filterEmptyData(this.searchForm)
        };
        const res = await getTrainingMaterialsList(params);
        const { records = [], current = 1, total = 0 } = res.data || {};
        this.materialsList = records;
        this.pagination.currentPage = current;
        this.pagination.total = total;
      } catch (error) {
        this.$message.error("加载资料列表失败");
        console.error(error);
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.loadMaterialsList();
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedMaterials = selection;
    },

    // 分页
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.loadMaterialsList();
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadMaterialsList();
    },

    // 获取资料类型标签样式
    getMaterialTypeTag(type) {
      const typeMap = {
        VIDEO: "primary",
        PDF: "success",
        IMAGE: "warning"
      };
      return typeMap[type] || "info";
    },

    // 确认选择
    handleConfirm() {
      if (this.selectedMaterials.length === 0) {
        this.$message.warning("请选择培训资料");
        return;
      }

      if (this.selectedMaterials.length > 1) {
        this.$message.warning("只能选择一个培训资料");
        return;
      }

      this.$emit("confirm", this.selectedMaterials[0]);
      this.handleClose();
    },

    // 关闭
    handleClose() {
      this.dialogVisible = false;
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style lang="less" scoped>
.materials-selector {
  .search-bar {
    display: flex;
    align-items: center;
  }

  .title-cell {
    display: flex;
    align-items: center;

    .title-text {
      font-weight: 500;
    }
  }

  .pagination-container {
    margin-top: 16px;
    text-align: right;
  }

  .text-muted {
    color: #909399;
  }
}

.dialog-footer {
  text-align: right;
}
</style>
